/**
 * Host player sheet. SPEC.md §30, §9.3, §9.4, §31, §49.
 *
 * Everything a host can do *to* one player: enter points as them, move their seat,
 * take them off the table — one section per picker in the host menu, which is the
 * only way in. `adminPlayerId` is the whole of its input. §30, §49
 *
 * Starting a hand is deliberately *not* here. A tap on a seat is what starts a hand
 * (§50), and offering the same action behind two taps as well would be the second
 * path this rework exists to remove. §4.3
 *
 * "Control Sarah" only sets ui-store's controllingPlayerId. The transfer that
 * results still records the host as `createdByPlayerId` and Sarah as
 * `actedForPlayerId` — the server does that, not this sheet. §30
 */

import { useEffect, useState } from 'react';
import {
  messageForErrorCode,
  nextFreeSeatIndex,
  type PlayerView,
} from '@card-counter/shared';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { OfflineFor } from '@/components/ui/OfflineFor';
import { Sheet } from '@/components/ui/Sheet';
import { useSession } from '@/hooks/useSession';
import {
  useCanEnterAs,
  useIsHost,
  useIsRoundActive,
  useMyPlayerId,
  usePlayerById,
  usePlayersBySeat,
  useWinnerPlayerId,
} from '@/stores/session-store';
import { useAdminPlayerId, useControllingPlayerId, useUiStore } from '@/stores/ui-store';

function seatLabel(seatIndex: number, occupant: PlayerView | null): string {
  const seat = `Seat ${String(seatIndex + 1)}`;
  return occupant ? `${seat} · ${occupant.name}` : `${seat} · empty`;
}

export function PlayerAdminSheet() {
  const adminPlayerId = useAdminPlayerId();
  const player = usePlayerById(adminPlayerId);
  const players = usePlayersBySeat();
  const myPlayerId = useMyPlayerId();
  const isHost = useIsHost();
  const roundActive = useIsRoundActive();
  const winnerPlayerId = useWinnerPlayerId();
  const controllingPlayerId = useControllingPlayerId();
  const canEnterAsThem = useCanEnterAs(adminPlayerId);

  const close = useUiStore((state) => state.closePlayerAdmin);
  const setControllingPlayer = useUiStore((state) => state.setControllingPlayer);
  const { moveSeat, removePlayer, interactive } = useSession();

  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [pending, setPending] = useState<'seat' | 'remove' | null>(null);

  // A destructive confirmation must never carry over to a different player. §31
  useEffect(() => {
    setConfirmingRemoval(false);
    setPending(null);
  }, [adminPlayerId]);

  // Host-only, and only ever presentation: the server re-checks every mutation. §39
  if (!isHost || !player) return null;

  const isSelf = player.id === myPlayerId;
  const isControlled = controllingPlayerId === player.id;
  // Someone who was not dealt into this hand has nothing for the host to report
  // on their behalf. §3.3
  const controlBlocked = roundActive && !canEnterAsThem;
  const isWinning = winnerPlayerId === player.id;

  const takenSeats = players.map((seated) => seated.seatIndex);
  const seatOptions = [...new Set([...takenSeats, nextFreeSeatIndex(takenSeats)])].sort(
    (a, b) => a - b,
  );
  const occupantBySeat = new Map(players.map((seated) => [seated.seatIndex, seated]));

  const move = async (toSeatIndex: number): Promise<void> => {
    setPending('seat');
    // The server swaps with whoever holds the seat and broadcasts the new order. §9.4
    await moveSeat(toSeatIndex, player.id);
    setPending(null);
  };

  const remove = async (): Promise<void> => {
    setPending('remove');
    const ok = await removePlayer(player.id);
    setPending(null);
    if (!ok) return;
    // Acting as someone who is no longer at the table makes no sense. §30
    if (isControlled) setControllingPlayer(null);
    close();
  };

  return (
    <Sheet
      open
      onClose={close}
      title={player.name}
      description={isSelf ? 'This is you.' : undefined}
    >
      <div className="flex flex-col gap-5 pb-2">
        <div className="flex items-center gap-3">
          <Avatar avatarKey={player.avatarKey} className="size-12 shrink-0" />
          <div className="flex min-w-0 flex-col gap-1">
            {/* Full name, untruncated: the ring is where names get shortened. §8.6 */}
            <p className="text-ink">{player.name}</p>
            <p className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
              {player.isHost ? <span>👑 Host</span> : null}
              {isWinning ? <span className="text-accent">👑 Winning this hand</span> : null}
              {player.presence === 'offline' ? (
                // §16.5 — they keep their seat, and the host can score for them.
                <OfflineFor since={player.lastSeenAt} />
              ) : null}
            </p>
          </div>
        </div>

        {isSelf ? null : (
          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold tracking-[0.2em] text-ink-faint uppercase">
              Enter for them
            </h3>
            {isControlled ? (
              <>
                <p className="text-sm text-ink-muted">
                  The keypad is entering points as {player.name}.
                </p>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => {
                    setControllingPlayer(null);
                    close();
                  }}
                >
                  Stop controlling {player.name}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  fullWidth
                  disabled={controlBlocked}
                  onClick={() => {
                    setControllingPlayer(player.id);
                    close();
                  }}
                >
                  Control {player.name}
                </Button>
                {controlBlocked ? (
                  <p role="status" className="text-sm text-warn">
                    {messageForErrorCode('FOCAL_PLAYER_CANNOT_SCORE')}
                  </p>
                ) : (
                  <p className="text-sm text-ink-muted">
                    The keypad will enter points as {player.name} until you stop.
                  </p>
                )}
              </>
            )}
          </section>
        )}

        <section className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold tracking-[0.2em] text-ink-faint uppercase">
            Seat
          </h3>
          {/* Swapping is the server's rule; this only names it for the host. §9.4 */}
          <p className="text-sm text-ink-muted">
            Tap a seat to move them there. If someone is already in it, the two swap and everyone's
            table updates.
          </p>
          <ul className="flex flex-col gap-1.5">
            {seatOptions.map((seatIndex) => {
              const occupant = occupantBySeat.get(seatIndex) ?? null;
              const current = seatIndex === player.seatIndex;
              return (
                <li key={seatIndex}>
                  <button
                    type="button"
                    aria-pressed={current}
                    disabled={current || !interactive || pending !== null}
                    onClick={() => {
                      void move(seatIndex);
                    }}
                    className={[
                      'flex min-h-tap w-full items-center gap-3 rounded-card border px-3 py-2 text-left',
                      'disabled:opacity-60',
                      current ? 'border-accent bg-accent-soft' : 'border-line bg-surface',
                    ].join(' ')}
                  >
                    <span className="min-w-0 flex-1 truncate text-sm text-ink">
                      {seatLabel(seatIndex, occupant)}
                    </span>
                    {current ? (
                      <span className="text-xs tracking-wide text-accent uppercase">Here now</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        {isSelf ? null : (
          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold tracking-[0.2em] text-ink-faint uppercase">
              Remove
            </h3>
            {confirmingRemoval ? (
              <>
                <p role="alert" className="text-sm text-ink">
                  Remove {player.name} from the table?
                </p>
                {/* §31 — removing during a live hand needs saying out loud. */}
                {roundActive ? (
                  <p className="text-sm text-warn">
                    A hand is in progress.
                    {isWinning
                      ? ` ${player.name} is the only player yet to report, so the hand will have no winner until somebody else withdraws.`
                      : ' The hand keeps the players it was dealt to, so its points stay as they are.'}
                  </p>
                ) : null}
                <p className="text-sm text-ink-muted">
                  Nothing is deleted: their points stay in the history and in the final score.
                </p>
                <Button
                  variant="danger"
                  fullWidth
                  loading={pending === 'remove'}
                  disabled={!interactive || pending === 'seat'}
                  onClick={() => {
                    void remove();
                  }}
                >
                  Yes, remove {player.name}
                </Button>
                <Button
                  variant="ghost"
                  fullWidth
                  onClick={() => {
                    setConfirmingRemoval(false);
                  }}
                >
                  Keep them at the table
                </Button>
              </>
            ) : (
              <Button
                variant="danger"
                fullWidth
                onClick={() => {
                  setConfirmingRemoval(true);
                }}
              >
                Remove {player.name}
              </Button>
            )}
          </section>
        )}
      </div>
    </Sheet>
  );
}
